/**
 * Scoring Configuration for UI Only
 * 
 * This file contains ONLY configuration for the UI:
 * - Strategy definitions (labels, descriptions, UI properties)
 * - Display formatting (colors, icons)
 * - Validation rules (for template editor)
 * 
 * NO CALCULATION LOGIC - All scoring happens on backend!
 */

// ============================================================================
// FIELD TYPES FOR UI
// ============================================================================

export const FIELD_TYPES = {
  TEXT: 'text',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  SELECT: 'select',
  TEXTAREA: 'textarea'
};

// ============================================================================
// SCORING STRATEGIES (UI Configuration Only)
// ============================================================================

export const SCORING_STRATEGIES = {
  Binary: {
    label: 'Pass/Fail (0 or 1)',
    description: 'Simple binary scoring - either pass (1) or fail (0)',
    icon: '✓',
    minScore: 0,
    maxScore: 1,
    defaultValue: 0,
    allowCustomRange: false,
    
    // UI validation only (backend does real validation)
    validateUI: (score) => score === 0 || score === 1,
    
    // Display formatting
    formatDisplay: (score) => score === 1 ? 'Pass' : 'Fail',
    getColor: (score) => score === 1 ? 'green' : 'red',
    
    // UI fields for configuration
    configFields: []
  },
  
  Scale5: {
    label: '5-Point Scale (1-5)',
    description: 'Standard 5-point rating scale (1=Poor, 5=Excellent)',
    icon: '⭐',
    minScore: 1,
    maxScore: 5,
    defaultValue: 3,
    allowCustomRange: false,
    
    validateUI: (score) => score >= 1 && score <= 5 && Number.isInteger(score),
    formatDisplay: (score) => `${score}/5`,
    getColor: (score) => {
      if (score >= 4) return 'green';
      if (score >= 3) return 'yellow';
      return 'red';
    },
    
    labels: {
      1: 'Poor',
      2: 'Below Average',
      3: 'Average',
      4: 'Good',
      5: 'Excellent'
    },
    
    configFields: []
  },
  
  Scale10: {
    label: '10-Point Scale (1-10)',
    description: 'Extended 10-point rating scale for more granular scoring',
    icon: '📊',
    minScore: 1,
    maxScore: 10,
    defaultValue: 5,
    allowCustomRange: false,
    
    validateUI: (score) => score >= 1 && score <= 10 && Number.isInteger(score),
    formatDisplay: (score) => `${score}/10`,
    getColor: (score) => {
      if (score >= 8) return 'green';
      if (score >= 5) return 'yellow';
      return 'red';
    },
    
    configFields: []
  },
  
  Percentage: {
    label: 'Percentage (0-100)',
    description: 'Percentage-based scoring from 0 to 100',
    icon: '%',
    minScore: 0,
    maxScore: 100,
    defaultValue: 50,
    allowCustomRange: false,
    
    validateUI: (score) => score >= 0 && score <= 100,
    formatDisplay: (score) => `${score}%`,
    getColor: (score) => {
      if (score >= 80) return 'green';
      if (score >= 60) return 'yellow';
      return 'red';
    },
    
    configFields: []
  },
  
  NPS: {
    label: 'Net Promoter Score (0-10)',
    description: 'NPS scoring: Detractors (0-6), Passives (7-8), Promoters (9-10)',
    icon: '📈',
    minScore: 0,
    maxScore: 10,
    defaultValue: 7,
    allowCustomRange: false,
    
    validateUI: (score) => score >= 0 && score <= 10 && Number.isInteger(score),
    formatDisplay: (score) => `${score}/10`,
    getColor: (score) => {
      if (score >= 9) return 'green';   // Promoter
      if (score >= 7) return 'yellow';  // Passive
      return 'red';                      // Detractor
    },
    
    labels: {
      0: 'Detractor', 1: 'Detractor', 2: 'Detractor',
      3: 'Detractor', 4: 'Detractor', 5: 'Detractor',
      6: 'Detractor', 7: 'Passive', 8: 'Passive',
      9: 'Promoter', 10: 'Promoter'
    },
    
    configFields: []
  },
  
  Custom: {
    label: 'Custom Range',
    description: 'Define your own min/max score range',
    icon: '🎯',
    minScore: null,
    maxScore: null,
    defaultValue: null,
    allowCustomRange: true,

    validateUI: (score, config) => {
      const min = config?.minScore ?? 0;
      const max = config?.maxScore ?? 10;
      return score >= min && score <= max;
    },

    formatDisplay: (score, config) => {
      const max = config?.maxScore ?? 10;
      return `${score}/${max}`;
    },

    getColor: (score, config) => {
      const min = config?.minScore ?? 0;
      const max = config?.maxScore ?? 10;
      const range = max - min;
      const normalized = (score - min) / range;

      if (normalized >= 0.8) return 'green';
      if (normalized >= 0.6) return 'yellow';
      return 'red';
    },

    configFields: [
      {
        key: 'minScore',
        label: 'Minimum Score',
        type: FIELD_TYPES.NUMBER,
        required: true
      },
      {
        key: 'maxScore',
        label: 'Maximum Score',
        type: FIELD_TYPES.NUMBER,
        required: true
      }
    ]
  },

  CustomWeight: {
    label: 'Custom Weight (%)',
    description: 'Score on a custom range with a specific percentage weight toward the final score',
    icon: '⚖️',
    minScore: null,
    maxScore: null,
    defaultValue: null,
    allowCustomRange: true,

    validateUI: (score, config) => {
      const min = config?.minScore ?? 1;
      const max = config?.maxScore ?? 5;
      return score >= min && score <= max;
    },

    formatDisplay: (score, config) => {
      const max = config?.maxScore ?? 5;
      const pct = config?.weightPct ?? 0;
      return `${score}/${max} (${pct}%)`;
    },

    getColor: (score, config) => {
      const min = config?.minScore ?? 1;
      const max = config?.maxScore ?? 5;
      const range = max - min;
      const normalized = range > 0 ? (score - min) / range : 0;

      if (normalized >= 0.8) return 'green';
      if (normalized >= 0.6) return 'yellow';
      return 'red';
    },

    configFields: [
      {
        key: 'minScore',
        label: 'Minimum Score',
        type: FIELD_TYPES.NUMBER,
        required: true
      },
      {
        key: 'maxScore',
        label: 'Maximum Score',
        type: FIELD_TYPES.NUMBER,
        required: true
      },
      {
        key: 'weightPct',
        label: 'Weight % toward final score',
        type: FIELD_TYPES.NUMBER,
        required: true
      }
    ]
  }
};

// ============================================================================
// SECTION SCORING METHODS (UI Configuration Only)
// ============================================================================

export const SCORING_METHODS = {
  Average: {
    label: 'Average (Equal Weight)',
    description: 'All questions weighted equally within section',
    icon: '='
  },
  
  WeightedAverage: {
    label: 'Weighted Average',
    description: 'Questions weighted by individual weights',
    icon: '⚖️'
  },
  
  Minimum: {
    label: 'Minimum (Strictest)',
    description: 'Section score is the lowest question score',
    icon: '⬇️'
  },
  
  Maximum: {
    label: 'Maximum (Most Lenient)',
    description: 'Section score is the highest question score',
    icon: '⬆️'
  },

  CustomWeight:{
    label: 'Custom Weight (%)',
    description: 'Questions weighted by individual weights with custom percentage toward section score',
    icon: '📐'
  }
};

// ============================================================================
// TEMPLATE SCORING MODES (UI Configuration Only)
// ============================================================================

export const TEMPLATE_SCORING_MODES = {
  Weighted: {
    label: 'Weighted Sections',
    description: 'Sections have different importance (weights must sum to 100)',
    icon: '⚖️',
    requiresWeights: false,
    requiresWeightSum: 100
  },
  
  Unweighted: {
    label: 'Unweighted Sections',
    description: 'All sections equally important (simple average)',
    icon: '📊',
    requiresWeights: false
  },

  CustomWeightQuestions: {
    label: 'Custom Weight Questions (%)',
    description: 'Questions use custom weight strategy with percentage toward final score',
    icon: '📐',
    requiresWeights: false,
    requiresquestionWeights: true,
    requiresquestionWeightSum: 100,
    forceScoringStrategy: 'CustomWeight'
  },
  
  PointBased: {
    label: 'Point-Based',
    description: 'Sum all question points (no section grouping)',
    icon: '🎯',
    requiresWeights: false
  },
  
  PassFail: {
    label: 'Pass/Fail Only',
    description: 'Binary result - all questions must meet threshold',
    icon: '✓',
    requiresWeights: false,
    configFields: [
      {
        key: 'passFailThreshold',
        label: 'Pass Threshold (0-1)',
        type: FIELD_TYPES.NUMBER,
        defaultValue: 0.6
      }
    ]
  },
  
  Hybrid: {
    label: 'Hybrid (Weighted + Unweighted)',
    description: 'Some sections weighted, others equal',
    icon: '🔀',
    requiresWeights: 'optional'
  }
};

// ============================================================================
// UI HELPER FUNCTIONS (No Calculation Logic!)
// ============================================================================

/**
 * Get scoring strategy configuration
 */
export const getScoringStrategy = (strategyName) => {
  return SCORING_STRATEGIES[strategyName] || SCORING_STRATEGIES.Scale5;
};

/**
 * Get all strategy options for dropdown
 * @param {string} [templateMode] - The current scoring mode of the template (optional)
 */
export const getStrategyOptions = (templateMode) => {
  let strategyKeys = Object.keys(SCORING_STRATEGIES);

  // Check if the template mode forces a specific scoring strategy
  const modeConfig = TEMPLATE_SCORING_MODES[templateMode];
  if (modeConfig?.forceScoringStrategy) {
    // Force lock to the specified strategy
    strategyKeys = [modeConfig.forceScoringStrategy];
  } else {
    // Exclude CustomWeight from other modes
    strategyKeys = strategyKeys.filter(key => key !== 'CustomWeight');
  }

  return strategyKeys.map(key => ({
    label: SCORING_STRATEGIES[key].label,
    value: key,
    description: SCORING_STRATEGIES[key].description,
    icon: SCORING_STRATEGIES[key].icon
  }));
};

/**
 * Get all scoring method options for dropdown
 */
export const getScoringMethodOptions = () => {
  return Object.keys(SCORING_METHODS).map(key => ({
    label: SCORING_METHODS[key].label,
    value: key,
    description: SCORING_METHODS[key].description,
    icon: SCORING_METHODS[key].icon
  }));
};

/**
 * Get all template scoring mode options for dropdown
 */
export const getScoringModeOptions = () => {
  return Object.keys(TEMPLATE_SCORING_MODES).map(key => ({
    label: TEMPLATE_SCORING_MODES[key].label,
    value: key,
    description: TEMPLATE_SCORING_MODES[key].description,
    icon: TEMPLATE_SCORING_MODES[key].icon
  }));
};

/**
 * Format score for display (UI only)
 */
export const formatScoreDisplay = (score, strategyName, config = {}) => {
  const strategy = getScoringStrategy(strategyName);
  
  if (typeof strategy.formatDisplay === 'function') {
    return strategy.formatDisplay(score, config);
  }
  
  return String(score);
};

/**
 * Get color for score display (UI only)
 */
export const getScoreColor = (score, strategyName, config = {}) => {
  const strategy = getScoringStrategy(strategyName);
  
  if (typeof strategy.getColor === 'function') {
    return strategy.getColor(score, config);
  }
  
  return 'gray';
};

/**
 * Validate score in UI (basic validation only)
 * Backend does the real validation!
 */
export const validateScoreUI = (score, strategyName, config = {}) => {
  const strategy = getScoringStrategy(strategyName);
  
  if (typeof strategy.validateUI === 'function') {
    return strategy.validateUI(score, config);
  }
  
  return true;
};

/**
 * Validate template configuration for UI
 * Backend does the real validation!
 */
export const validateTemplateUI = (template, mode) => {
  const scoringMode = TEMPLATE_SCORING_MODES[mode];
  
  if (!scoringMode) {
    return { valid: false, message: 'Invalid scoring mode' };
  }
  
  if (!template.sections || template.sections.length === 0) {
    return { valid: false, message: 'Template must have at least one section' };
  }
  
  // Check if weights are required
  if (scoringMode.requiresWeights === true) {
    const hasWeights = template.sections.every(s => 
      s.weight !== undefined && s.weight !== null
    );
    
    if (!hasWeights) {
      return {
        valid: false,
        message: `${scoringMode.label} requires all sections to have weights`
      };
    }
    
    // Check if weights sum to required amount
    if (scoringMode.requiresWeightSum) {
      const totalWeight = template.sections.reduce((sum, s) => sum + (s.weight || 0), 0);
      
      if (Math.abs(totalWeight - scoringMode.requiresWeightSum) > 0.01) {
        return {
          valid: false,
          message: `Section weights must sum to ${scoringMode.requiresWeightSum} (currently ${totalWeight})`
        };
      }
    }
  }

  // Check if question weights are required (for CustomWeightQuestions mode)
  if (scoringMode.requiresquestionWeights === true) {
    // Get all questions from template
    const allQuestions = template.questions || [];
    
    console.log('CustomWeightQuestions validation - Total questions:', allQuestions.length);
    
    // Filter to only Quality questions (Compliance questions don't contribute to quality score)
    const qualityQuestions = allQuestions.filter(q => q.type === 'Quality');
    
    console.log('Quality questions:', qualityQuestions.length);
    
    // Check that all quality questions have CustomWeight strategy
    const nonCustomWeightQuestions = qualityQuestions.filter(q => 
      q.scoringStrategy !== 'CustomWeight'
    );
    
    console.log('Non-CustomWeight quality questions:', nonCustomWeightQuestions.length);
    
    if (nonCustomWeightQuestions.length > 0) {
      return {
        valid: false,
        message: `${scoringMode.label} requires all quality questions to use CustomWeight strategy. Found ${nonCustomWeightQuestions.length} question(s) with different strategies.`
      };
    }
    
    // Check that all quality questions have weightPct configured
    const questionsWithoutWeight = qualityQuestions.filter(q => 
      !q.scoringConfig || q.scoringConfig.weightPct === undefined || q.scoringConfig.weightPct === null || q.scoringConfig.weightPct === ''
    );
    
    console.log('Quality questions without weight:', questionsWithoutWeight.length);
    
    if (questionsWithoutWeight.length > 0) {
      return {
        valid: false,
        message: `${scoringMode.label} requires all quality questions to have a weight percentage. ${questionsWithoutWeight.length} question(s) missing weight.`
      };
    }
    
    // Check if quality question weights sum to required amount
    if (scoringMode.requiresquestionWeightSum) {
      const totalQuestionWeight = qualityQuestions.reduce((sum, q) => {
        const weightPct = parseFloat(q.scoringConfig?.weightPct || 0);
        console.log(`Question ${q.questionName} (${q.type}): weightPct = ${weightPct}`);
        return sum + weightPct;
      }, 0);
      
      console.log(`Total quality question weight: ${totalQuestionWeight}, Required: ${scoringMode.requiresquestionWeightSum}`);
      
      if (Math.abs(totalQuestionWeight - scoringMode.requiresquestionWeightSum) > 0.01) {
        return {
          valid: false,
          message: `Quality question weights must sum to ${scoringMode.requiresquestionWeightSum}% (currently ${totalQuestionWeight.toFixed(2)}%). Note: Compliance questions are not included in this total.`
        };
      }
    }
  }
  
  return { valid: true };
};

/**
 * Auto-detect appropriate scoring mode based on template structure
 * (UI helper only - backend makes final decision)
 */
export const detectScoringModeUI = (template) => {
  if (!template.sections || template.sections.length === 0) {
    return 'Unweighted';
  }
  
  const sectionsWithWeights = template.sections.filter(s => 
    s.weight !== undefined && s.weight !== null && s.weight > 0
  );
  
  // All sections have weights
  if (sectionsWithWeights.length === template.sections.length) {
    const totalWeight = sectionsWithWeights.reduce((sum, s) => sum + s.weight, 0);
    
    if (Math.abs(totalWeight - 100) < 0.01) {
      return 'Weighted';
    }
  }
  
  // No sections have weights
  if (sectionsWithWeights.length === 0) {
    return 'Unweighted';
  }
  
  // Mix of weighted and unweighted
  return 'Hybrid';
};

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

export const DEFAULT_SCORING_CONFIG = {
  strategy: 'Scale5',
  method: 'Average',
  mode: 'Weighted'
};

export const COMPLIANCE_DEFAULT_CONFIG = {
  strategy: 'Binary',
  autofail: false
};

export const QUALITY_DEFAULT_CONFIG = {
  strategy: 'Scale5',
  weight: 10,
  method: 'Average'
};

export const CSAT_DEFAULT_CONFIG = {
  strategy: 'NPS',
  sentimentTarget: 'Positive'
};

// ============================================================================
// NOTES
// ============================================================================

/**
 * IMPORTANT: This file contains NO calculation logic!
 * 
 * All actual scoring calculations happen on the backend in:
 * - scoring_strategies.py (question-level)
 * - scoring_modes.py (template-level)
 * 
 * This file only provides:
 * - UI configuration (labels, descriptions, icons)
 * - Display formatting (colors, formatting)
 * - Basic UI validation (for user feedback)
 * - Dropdown options
 * 
 * The backend is the source of truth for all calculations.
 */
